import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
	ChatManagerEvents,
	IChat,
	ListenerItem,
	TCacheMessagesByDate,
	TMessagesByDate,
} from "@dreamcam/messenger-sdk";
import { Map as ImmutableMap, Set as ImmutableSet } from "immutable";
import { X } from "lucide-react";
import { createPortal } from "react-dom";

import { Badge } from "@components/Badge";
import { Image } from "@components/Image";
import { onToast } from "@components/Toast";
import { Typography } from "@components/Typography";

import { useLayoutBreakpoint } from "@hooks/useLayoutBreakpoint";

import { userAppInfoStore } from "@storage/global/useAppInfoStore";
import { useDialogStore } from "@storage/global/useDialogStore";
import { useMessengerStore } from "@storage/global/useMessengerStore";

import { sdkMessenger } from "@services/messenger-sdk";

import { isAppleDevice } from "@utils/deviceDetect";
import { EBrowser } from "@utils/getBrowserInfo";

import { DELAY_IMMEDIATE, DELAY_TIME_100, DELAY_TIME_2000, DELAY_TIME_3000 } from "@configs/constants";
import { ICON_PATHS } from "@configs/icons";

import { ChatContainer } from "./ChatContainer";
import { ChatList } from "./ChatList";
import { throttle } from "./helpers";

const PARAM_LIMIT_CHATS = 999;
const PARAM_LIMIT_MESSAGES = 50;
const PARAM_PRELOAD_MESSAGES = 50;

export const MessengerContainer = memo(() => {
	const [chatList, setChatList] = useState<IChat[]>([]);
	const [messageListByDate, setMessageListByDate] = useState<TCacheMessagesByDate>();
	const [typingStatusList, setTypingStatusList] = useState<ImmutableSet<string>>(ImmutableSet());
	const [isLoaderMessages, setIsLoaderMessages] = useState<boolean>(false);
	const [isMoreMessagesMap, setIsMoreMessagesMap] = useState<TMoreMessagesMap>(ImmutableMap());
	const [textInputMessage, setTextInputMessage] = useState<string>("");
	const [isSendMessageError, setIsSendMessageError] = useState<boolean>(false);
	const [cacheScrollPosition, setCacheScrollPosition] = useState<ImmutableMap<string, number | string>>(ImmutableMap());
	const [isShowGoDownButton, setIsShowGoDownButton] = useState<boolean>(false);
	const [isLoadingChat, setIsLoadingChat] = useState<boolean>(false);
	const [isLoadingChatList, setIsLoadingChatList] = useState<boolean>(false);

	const sdk = sdkMessenger();
	const { userMsgData, activeChat, setActiveChat, countUnreadMessages, setCountUnreadMessages, isMessagesBoxFolded } =
		useMessengerStore();
	const containerRef = useRef<HTMLDivElement>(null);
	const chatManagerListenersRef = useRef<ListenerItem<ChatManagerEvents>[]>([]);
	const { messengerUnsupportedByUser, isOpenMessenger, setIsOpenMessenger } = useMessengerStore();
	const dialogStore = useDialogStore();
	const { currentBrowser } = userAppInfoStore();
	const { MOBILE, TABLET } = useLayoutBreakpoint();
	const isMobile = MOBILE || TABLET;

	const getAllChatsAsync = useCallback(async () => {
		setIsLoadingChatList(true);
		if (userMsgData.chat_client_id) {
			try {
				await sdk.chatManager.getChatsAsync(PARAM_LIMIT_CHATS).finally(() => setIsLoadingChatList(false));
			} catch (error) {
				const typedError = error as { status: number; message: string };
				onToast({
					message: "Error",
					description: typedError?.message,
				});
				setIsLoadingChatList(false);
			}
		}
	}, []);

	const getAllMessagesAsync = useCallback(
		async (
			chatId: string,
			isMore: boolean,
			relatedId: string | null = null,
			position: "forward" | "backward" = "forward",
		) => {
			setIsLoaderMessages(true);

			try {
				const messageList = await sdk.chatManager
					.getMessagesAsync(
						chatId,
						position,
						PARAM_LIMIT_MESSAGES,
						relatedId !== null ? 1 : PARAM_PRELOAD_MESSAGES,
						relatedId === null ? undefined : relatedId,
					)
					.catch((err) => {
						throw new Error(err);
					});

				if (isMore && messageList.length - 1 < PARAM_LIMIT_MESSAGES && position === "forward") {
					setIsMoreMessagesMap((prev) => prev.set(chatId, { ...prev.get(chatId)!, down: false }));
				} else if (isMore && messageList.length - 1 < PARAM_LIMIT_MESSAGES && position === "backward") {
					setIsMoreMessagesMap((prev) => prev.set(chatId, { ...prev.get(chatId)!, up: false }));
				}
			} catch (error) {
				const typedError = error as { status: string; message: string };
				onToast({
					message: typedError.status || "Error",
					description: typedError?.message,
				});
			}

			setIsLoaderMessages(false);
		},
		[messageListByDate],
	);

	const sendMessageAsync = async (message: string) => {
		try {
			if (activeChat !== null) {
				await sdk.chatManager.sendMessageAsync(activeChat, message.trim()).then(() => {
					throttleUpdateDraftMessageAsync(activeChat, "");
					setIsSendMessageError(false);
				});
			}
		} catch (error) {
			setIsSendMessageError(true);
			const typedError = error as { status: string; message: string };
			onToast({
				message: typedError.status || "Error",
				description: typedError?.message,
			});
			throw new Error("", { cause: error });
		}
	};

	const onStartedTypingThrottle = useCallback(
		throttle((chatId: string | null, isMessageWasSent: boolean) => {
			if (isMessageWasSent) return;
			if (chatId) {
				sdk.chatManager.sendStartedTypingAsync(chatId);
			}
		}, DELAY_TIME_2000),
		[activeChat],
	);

	const throttleUpdateDraftMessageAsync = useCallback(
		throttle(async (chatId: string, message: string) => {
			try {
				if (chatId !== null) {
					await sdk.chatManager.updateDraftMessageAsync(chatId, message);
				}
			} catch (error) {
				const typedError = error as { status: string; message: string };
				onToast({
					message: typedError.status || "Error",
					description: typedError?.message,
				});
			}
		}, DELAY_TIME_3000),
		[activeChat],
	);

	const subscribeSdkForEvents = (isShowGoDownButton: boolean, isMessagesBoxFolded: boolean) => {
		chatManagerListenersRef.current.push(
			sdk.chatManager.registerListener("onUpdateCountUnreadMessages", (args) => {
				setCountUnreadMessages(args.count);
			}),
		);
		chatManagerListenersRef.current.push(
			sdk.chatManager.registerListener("onMessageStateChanged", (args) => {
				setMessageListByDate(sdk.chatManager.messagesByDate);

				if (args.event === "SENT" && !isMessagesBoxFolded) {
					const findChatContainer = document.getElementById("chat-container");

					if (findChatContainer) {
						if (currentBrowser === EBrowser.Chrome) {
							findChatContainer.style.scrollBehavior = "smooth";
						}

						const isCompanionCondition = args.sender_id !== userMsgData.chat_client_id && !isShowGoDownButton;
						const isPersonalCondition = args.sender_id === userMsgData.chat_client_id;

						if (isCompanionCondition || isPersonalCondition) {
							if (isAppleDevice()) {
								const observer = new MutationObserver(() => {
									findChatContainer.scrollTop = findChatContainer.scrollHeight;
								});

								observer.observe(findChatContainer, { childList: true, subtree: true });

								return () => observer.disconnect();
							} else {
								setTimeout(() => {
									findChatContainer.scrollTop = findChatContainer.scrollHeight;
								}, DELAY_IMMEDIATE);
							}
						}
					}
				}
			}),
		);
		chatManagerListenersRef.current.push(
			sdk.chatManager.registerListener("onMessageListByDateChanged", () => {
				setMessageListByDate(sdk.chatManager.messagesByDate);
			}),
		);

		chatManagerListenersRef.current.push(
			sdk.chatManager.registerListener("onChatListChanged", () => {
				setChatList(sdk.chatManager.chats);
			}),
		);
		chatManagerListenersRef.current.push(
			sdk.chatManager.registerListener("onChatChanged", (args) => {
				if (args.event === "DRAFT") {
					if (activeChat === args.chatData.id) {
						setTextInputMessage(args.chatData.draft_message.content);
					}
				} else if (args.event === "TYPING") {
					if (args.isTyping) {
						setTypingStatusList((prev) => prev.add(args.chatData.id));
					} else {
						setTypingStatusList((prev) => prev.filter((chatId) => chatId !== args.chatData.id));
					}
				}
			}),
		);
	};

	const unsubscribeAllEvents = () => {
		sdk.chatManager.unregisterListeners(chatManagerListenersRef.current);
		chatManagerListenersRef.current = [];
	};

	const setActivePanel = (isOpenChatList: boolean, currentChat: string | null) => {
		setIsOpenMessenger(isOpenChatList);
		setActiveChat(currentChat);
	};

	const setActiveChatInfo: IChat | null = useMemo(() => {
		if (activeChat) {
			return chatList.find((chat) => chat.id === activeChat) || null;
		} else {
			return null;
		}
	}, [chatList, activeChat]);

	const clickOutside = useCallback(
		(event: MouseEvent): void => {
			const target = event.target as Element;
			const findDropdownMenu = document.getElementById("dropdown-menu-msg-chat-content");

			if (
				isOpenMessenger &&
				containerRef.current &&
				!containerRef.current.contains(target) &&
				activeChat === null &&
				!messengerUnsupportedByUser.isDisabled &&
				!dialogStore.isOpen &&
				!findDropdownMenu
			) {
				setActivePanel(false, null);
			}
		},
		[setActivePanel, isOpenMessenger, dialogStore],
	);

	useEffect(() => {
		document.addEventListener("mousedown", clickOutside);

		return () => {
			document.removeEventListener("mousedown", clickOutside);
		};
	}, [clickOutside, isOpenMessenger]);

	useEffect(() => {
		subscribeSdkForEvents(isShowGoDownButton, isMessagesBoxFolded);

		return () => {
			unsubscribeAllEvents();
		};
	}, [messageListByDate, chatList, activeChat, isShowGoDownButton, isMessagesBoxFolded]);

	useEffect(() => {
		setTimeout(() => {
			getAllChatsAsync();
		}, DELAY_TIME_100);
	}, [getAllChatsAsync]);

	const loadAllMessages = useCallback(
		(activeChat: string, direction?: "forward" | "backward", relatedID?: string) => {
			setIsSendMessageError(false);
			setIsShowGoDownButton(false);

			if (messageListByDate?.get(activeChat) === undefined) {
				setIsLoadingChat(true);
				setTimeout(() => {
					getAllMessagesAsync(activeChat, true, relatedID, direction).finally(() => {
						setIsLoadingChat(false);
					});
				}, DELAY_IMMEDIATE);
			}

			const findDraftMessage = chatList.find((chat) => chat.id === activeChat);
			const getDraftMessage = findDraftMessage?.draft_message.content || "";
			if (findDraftMessage?.id === activeChat) setTextInputMessage(getDraftMessage);
			if (isMoreMessagesMap.get(activeChat) === undefined) {
				setIsMoreMessagesMap((prev) => prev.set(activeChat, { up: true, down: true }));
			}
		},
		[activeChat],
	);

	useEffect(() => {
		if (activeChat !== null) {
			loadAllMessages(activeChat);
		}
	}, [activeChat, loadAllMessages]);

	return (
		<div className="inline-block">
			<span className="relative cursor-pointer" onClick={() => setIsOpenMessenger(true)}>
				<Image width={40} height={40} src={ICON_PATHS.MSG_ICON} alt="msg_icon" />
				{countUnreadMessages > 0 ? (
					<Badge variant="error" className="absolute top-1 right-0 h-3 w-3 rounded-[50%] p-0" />
				) : null}
			</span>

			{isOpenMessenger ? (
				<div
					className={
						isMobile
							? `fixed top-0 left-0 z-max flex h-screen w-screen overflow-hidden bg-black/90 transition-all duration-300
								ease-[ease]`
							: "absolute right-0 z-sticky mt-2.5 w-107.75 shadow-[inset_0_0_8px_8px_rgba(0,0,0,1)]"
					}
					ref={containerRef}
				>
					<div
						className={
							isMobile
								? "w-full bg-block"
								: "absolute w-full cursor-auto rounded-xl bg-block shadow-[0_2px_20px_rgba(0,0,0,0.9)]"
						}
					>
						<div
							className={
								chatList.length > 0 ? "messenger-content-scrollbar overflow-hidden overflow-y-auto rounded-xl" : ""
							}
							style={!isMobile ? { height: "calc(100vh - 156px)" } : { height: "100%" }}
						>
							<nav
								className={`flex min-w-107.75 flex-col gap-2.5 overflow-hidden rounded-l-[10px] border-r border-block
									bg-block`}
								style={isMobile ? { minWidth: "100%" } : {}}
							>
								<div className="flex items-center justify-between px-6 py-5">
									<Typography type="span" size="xl" weight="bold" className="tracking-tighter" padding="none">
										Messages
									</Typography>

									<X
										strokeWidth={1.5}
										stroke="white"
										size={30}
										onClick={() => setIsOpenMessenger(false)}
										className="cursor-pointer hover:opacity-60"
									/>
								</div>

								{chatList.length > 0 ? (
									<ChatList
										chatList={chatList}
										activeChat={activeChat}
										setActivePanel={setActivePanel}
										typingStatusList={typingStatusList}
										containerRef={containerRef}
									/>
								) : null}
							</nav>

							{chatList.length === 0 && !isLoadingChatList ? (
								<div className="flex h-full flex-col items-center justify-center">
									<Image width={83} height={74} src={ICON_PATHS.NO_MESSAGES} alt="no_messages" />
									<Typography type="span" size="3xl" weight="bold">
										No messages yet
									</Typography>
								</div>
							) : null}
						</div>
					</div>
				</div>
			) : null}

			<>
				{activeChat !== null || messengerUnsupportedByUser.isDisabled ? (
					<>
						{createPortal(
							<ChatContainer
								messageListByDate={messageListByDate?.get(activeChat as string) || (ImmutableMap() as TMessagesByDate)}
								sendMessageAsync={sendMessageAsync}
								activeChat={activeChat as string}
								throttleUpdateDraftMessageAsync={throttleUpdateDraftMessageAsync}
								onStartedTypingThrottle={onStartedTypingThrottle}
								getAllMessagesAsync={getAllMessagesAsync}
								isLoaderMessages={isLoaderMessages}
								isMoreMessagesMap={isMoreMessagesMap}
								textInputMessage={textInputMessage}
								chatInfo={setActiveChatInfo}
								isSendMessageError={isSendMessageError}
								cacheScrollPosition={cacheScrollPosition}
								setCacheScrollPosition={setCacheScrollPosition}
								setActivePanel={setActivePanel}
								isShowGoDownButton={isShowGoDownButton}
								setIsShowGoDownButton={setIsShowGoDownButton}
								isLoadingChat={isLoadingChat}
								typingStatusList={typingStatusList}
							/>,
							document.body,
						)}
					</>
				) : null}
			</>

			<style>
				{(isOpenMessenger || activeChat !== null) && isMobile
					? `
						body, main {
							overflow: auto;
							overflow-x: hidden;
							-webkit-transform: translate(0, 0);
							touch-action: none;
						}

						footer, main {
							display: none !important;
						}
					`
					: ""}
			</style>
		</div>
	);
});

export type TListEvents = "WAITING" | "TYPING" | "WRITING";
export enum ECurrentEvent {
	TYPING = "TYPING",
	WAITING = "WAITING",
	WRITING = "WRITING",
}
export type TMoreMessagesMap = ImmutableMap<string, Record<"up" | "down", boolean>>;
